import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Palette, Zap, Plus } from "lucide-react";
import { SectionErrorBoundary } from "@/components/ErrorBoundary";
import { ArtworkGroupCard } from "@/components/quote";

export const PrintGroupsCard = ({
  job,
  setJob,
  setHasUnsavedChanges,
  expandedSections,
  toggleSection,
  selectedTemplate,
  setSelectedTemplate,
  PLACEMENT_TEMPLATES,
  handleApplyTemplate,
  addArtworkGroup,
  showTemplateConfirm,
  setShowTemplateConfirm,
  applyPlacementTemplate,
  jobId,
  jobNumber,
  updateArtworkGroup,
  updateArtworkGroupFields,
  updateArtworkAllocation,
  removeArtworkGroup,
  quote,
  isAdmin,
  showInternal,
}) => {
  return (
            <Collapsible 
              open={expandedSections.artworkGroups} 
              onOpenChange={() => toggleSection('artworkGroups')}
            >
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm" data-testid="artwork-groups-card">
              <CollapsibleTrigger asChild>
              <div className="px-5 py-4 cursor-pointer hover:bg-gray-50/50 transition-colors rounded-t-lg">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    {expandedSections.artworkGroups ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <Palette className="w-4 h-4 text-blue-500" />
                    Print Groups
                    {job.artwork_groups.length > 0 && (
                      <span className="text-xs text-gray-500 font-normal ml-1">({job.artwork_groups.length})</span>
                    )}
                  </h3>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {/* Placement Template Selector */}
                    <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                      <SelectTrigger className="w-[140px] h-8 text-xs border-gray-200" data-testid="template-selector">
                        <SelectValue placeholder="Template" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(PLACEMENT_TEMPLATES).map((template) => (
                          <SelectItem key={template} value={template} className="text-xs">
                            {template}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleApplyTemplate}
                      size="sm"
                      variant="outline"
                      disabled={selectedTemplate === "Custom"}
                      data-testid="apply-template-btn"
                      className="h-8 text-xs border-gray-200"
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Apply
                    </Button>
                    <Button
                      onClick={addArtworkGroup}
                      size="sm"
                      data-testid="add-artwork-btn"
                      className="h-8 text-xs bg-blue-500 hover:bg-blue-600"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
              <div className="px-5 pb-5 border-t border-gray-100">
                {/* Template Confirmation Dialog */}
                {showTemplateConfirm && (
                  <div className="mt-4 mb-4 p-3 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-800 mb-3">
                      You have existing artwork groups. How would you like to apply the template?
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => applyPlacementTemplate("add")}
                        data-testid="template-add-btn"
                        className="h-8 text-xs"
                      >
                        Add to Existing
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-100"
                        onClick={() => applyPlacementTemplate("replace")}
                        data-testid="template-replace-btn"
                      >
                        Replace All
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-xs"
                        onClick={() => setShowTemplateConfirm(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {job.artwork_groups.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Palette className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No print groups added yet</p>
                    <p className="text-xs">Click "Add Artwork" to start</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {job.artwork_groups.map((artwork, index) => (
                      <SectionErrorBoundary 
                        key={artwork.id}
                        sectionName={`Artwork: ${artwork.placement_name || `Group ${index + 1}`}`}
                        jobId={jobId}
                        jobNumber={jobNumber}
                        fallbackHeight="120px"
                      >
                        <ArtworkGroupCard
                          artwork={artwork}
                          index={index}
                          onUpdate={(field, value) =>
                            updateArtworkGroup(artwork.id, field, value)
                          }
                          onUpdateFields={(fields) =>
                            updateArtworkGroupFields(artwork.id, fields)
                          }
                          onUpdateAllocation={updateArtworkAllocation}
                          onRemove={() => removeArtworkGroup(artwork.id)}
                          garments={job.garments}
                          jobId={jobId}
                          breakdown={quote?.artwork_breakdown?.find(
                            (b) => b.id === artwork.id
                          )}
                          showInternal={isAdmin && showInternal}
                        />
                      </SectionErrorBoundary>
                    ))}
                  </div>
                )}
              </div>
              </CollapsibleContent>
            </div>
            </Collapsible>
  );
};
